import { QueryResult } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import styled from '@emotion/styled/macro';
import { differenceInMinutes } from 'date-fns';
import { findLast, path, propEq } from 'ramda';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { ChatBubble } from '../components';
import TypingBubble from '../components/TypingBubble';
import { GetChannelQuery, GetMessagesQuery } from '../generated/hooks';
import { channelQuery } from '../graphql/queries';
import { TABS } from '../overview/Overview';
import { IChannel } from '../types';

const Content = styled.div`
  max-width: 960px;
  width: 90vw;
  margin-left: auto;
  margin-right: auto;
  padding: 0 12px 40px;
  flex: 1 0 auto;
`;

interface IProps {
  typing: boolean;
  messages: NonNullable<GetMessagesQuery['messages']>;
  notDeliveredMessages: NonNullable<GetMessagesQuery['messages']>;
  channel: IChannel;
  sendMessage: (message: string, index?: number, values?: number[], id?: string) => void;
  disabled?: boolean;
  displayLinkToOneplace: boolean;
  someUnfilledChannel: boolean;
  someReport: boolean;
  /** End-of-thread sentinel in the scrollable column (for autoscroll). */
  messageListEndRef?: React.Ref<HTMLDivElement>;
  /** Last “chat” block before Specials/Input: last bubble, or typing, or last not-delivered. */
  lastPreControlsBlockRef?: React.Ref<HTMLDivElement>;
}

const Messages = ({
  typing,
  messages,
  notDeliveredMessages,
  channel,
  sendMessage,
  disabled,
  displayLinkToOneplace,
  someUnfilledChannel,
  someReport,
  messageListEndRef,
  lastPreControlsBlockRef,
}: IProps) => {
  const { t } = useTranslation('bubble');
  const history = useHistory();

  return (
    <Query<any, any> query={channelQuery} variables={{ channelId: channel.id }} fetchPolicy="cache-only">
      {({ data }: QueryResult<GetChannelQuery>) => {
        let prevDate = '1970-01-01'; // to show datetime in first message
        const chan = path<GetChannelQuery['channel']>(['channel'], data);
        const anonymityLevel = (chan && chan.anonymityLevel) || channel.anonymityLevel;
        let lastRespondentMessage: NonNullable<GetMessagesQuery['messages']>[0] | undefined;
        if (messages) {
          lastRespondentMessage = findLast(propEq('createdBy', 'RESPONDENT'), messages);
        }
        const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
        // Arnold is waiting for answer if last message is type of Question
        const editDisabled =
          lastMessage != null
            ? lastMessage.__typename !== 'Question' &&
              // allow editing last answer for testing channels
              (!channel.testing || lastMessage.__typename !== 'EndingMessage')
            : true;

        const display =
          displayLinkToOneplace &&
          (channel.isFinished || messages?.some((m) => m.isLast || m.__typename === 'EndingMessage'));

        const fakeMessage = {
          __typename: 'OtherMessage',
          id: 'FAKE',
          text: t(
            someUnfilledChannel
              ? 'onePlaceInfoWithunfilledConversations'
              : someReport
                ? 'onePlaceInfoReport'
                : 'onePlaceInfo',
          ),
          createdAt: lastMessage?.createdAt ?? new Date().toISOString(),
          isLast: null,
          createdBy: 'BOT',
          anonymityLevel: 'NONE',
        };
        const messagesToRender: NonNullable<GetMessagesQuery['messages']> = display
          ? [...messages, fakeMessage as any]
          : messages;
        const lastIndexMsg = messagesToRender.length - 1;
        return (
          <Content>
            {messagesToRender.map((message: any, index: number) => {
              const showDate =
                differenceInMinutes(new Date(message.createdAt).getTime(), new Date(prevDate).getTime()) > 10;
              prevDate = message.createdAt;
              const isTailAnchor =
                lastPreControlsBlockRef && !typing && notDeliveredMessages.length === 0 && index === lastIndexMsg;
              return (
                <div key={message.id} ref={isTailAnchor ? lastPreControlsBlockRef : undefined}>
                  <ChatBubble
                    message={message}
                    previousMessage={messagesToRender[index - 1]}
                    nextMessage={messagesToRender[index + 1]}
                    showDate={showDate}
                    anonymityLevel={anonymityLevel}
                    index={index}
                    channel={channel}
                    isLastAnswer={lastRespondentMessage && lastRespondentMessage.id === message.id}
                    // Enable editing only when user is supposed to answer some question,
                    // otherwise we could delete some messages while arnold is sending messages
                    // which is not supported
                    hideEdit={editDisabled}
                    editDisabled={disabled}
                  />
                </div>
              );
            })}
            {notDeliveredMessages.map((message: any, index: number) => {
              const isTailAnchor = lastPreControlsBlockRef && !typing && index === notDeliveredMessages.length - 1;
              return (
                <div key={message.id} ref={isTailAnchor ? lastPreControlsBlockRef : undefined}>
                  <ChatBubble
                    message={message}
                    anonymityLevel={anonymityLevel}
                    error={true}
                    sentRepeat={sendMessage}
                    editDisabled={disabled}
                    hideEdit={editDisabled}
                    index={index + 1}
                  />
                </div>
              );
            })}
            {typing && lastPreControlsBlockRef && (
              <div ref={lastPreControlsBlockRef}>
                <TypingBubble />
              </div>
            )}
            {typing && !lastPreControlsBlockRef && <TypingBubble />}
            {display && (
              <Button
                variant="primary"
                className={'ml-9 mt-4'}
                type="button"
                onClick={() => {
                  history.push(`/chat-overview/${channel.id}/${someUnfilledChannel ? TABS.ANSWERS : ''}`);
                }}
              >
                {t(someReport && !someUnfilledChannel ? 'goToOneplaceForReports' : 'goToOneplace')}
              </Button>
            )}
            {messageListEndRef && <div ref={messageListEndRef} style={{ clear: 'both', height: 0 }} aria-hidden />}
          </Content>
        );
      }}
    </Query>
  );
};

export default Messages;
